import { useCallback, useContext, useMemo, useSyncExternalStore } from 'react'
import {
    ApiShopperLandingPageLink,
    DYNAMIC_GRID_EXCLUSION_TAG,
} from '@storefronts/common-types'
import { getRecentlyViewedSnapshot, RecentlyViewedProduct } from '../storage'
import { ViddyLPContext } from '../context/viddyLPContext'
import { PageContext } from '../context/PageContextProvider'
import { LandingPageLinksContext } from '../context/LandingPageLinksContext'

/**
 * Checks if a product should be excluded from dynamic grids based on its tags.
 */
function shouldExcludeFromDynamicGrids(
    product: ApiShopperLandingPageLink,
): boolean {
    return product.shopifyTags?.includes(DYNAMIC_GRID_EXCLUSION_TAG) ?? false
}

const EMPTY_RECENTLY_VIEWED: RecentlyViewedProduct[] = []

/** sessionStorage is fixed for the loaded document; no client store updates needed. */
function subscribeToNothing(_onStoreChange: () => void): () => void {
    return () => {}
}

function getServerSnapshot(): RecentlyViewedProduct[] {
    return EMPTY_RECENTLY_VIEWED
}

export const useRecentlyViewedProducts = (
    maxProducts: number = 10,
): ApiShopperLandingPageLink[] => {
    const { landingPage } = useContext(ViddyLPContext)
    const { activeLPLink } = useContext(PageContext)
    const { resolvedLandingPageLinks } = useContext(LandingPageLinksContext)

    // Reads sessionStorage via useSyncExternalStore so the server/hydration
    // snapshot is always `[]`, avoiding a hydration mismatch for returning
    // visitors who have real recently-viewed products on the client.
    const getSnapshot = useCallback(
        () => getRecentlyViewedSnapshot(landingPage.landingPageHash),
        [landingPage.landingPageHash],
    )
    const recentlyViewed = useSyncExternalStore(
        subscribeToNothing,
        getSnapshot,
        getServerSnapshot,
    )

    // Create a lookup map for fast matching (performance optimization)
    const linkHashMap = useMemo(() => {
        const map = new Map<string, ApiShopperLandingPageLink>()
        resolvedLandingPageLinks.forEach((link) => {
            map.set(link.linkHash, link)
        })
        return map
    }, [resolvedLandingPageLinks])

    return useMemo(() => {
        const currentLinkHash = activeLPLink?.linkHash

        // Only show products that exist in current page's landingPageLinks
        // This is clean and simple - no hacks, no enrichment, just real product data
        return recentlyViewed
            .filter((item) => item.linkHash !== currentLinkHash) // Filter out current product
            .map((item) => linkHashMap.get(item.linkHash)) // Lookup full product data
            .filter((link): link is ApiShopperLandingPageLink => !!link) // Remove products not on this page
            .filter((link) => !shouldExcludeFromDynamicGrids(link)) // Filter out excluded products
            .slice(0, maxProducts)
    }, [recentlyViewed, maxProducts, activeLPLink?.linkHash, linkHashMap])
}
